// Camera for the mission map: an SVG viewBox plus an HTML overlay that is re-projected
// on every frame. Handles fly-to animation, wheel zoom, drag pan, double-click zoom
// and keyboard control. Pure UI — knows nothing about progress.

const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);
const reduceMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
export const MIN_W = 2.4;
export const MAX_W = 2600;

export function createCamera(stage, svg, { onFrame, onUserMove } = {}) {
  let vb = { x: 0, y: 0, w: 1000, h: 1000 };
  let anim = null;
  const size = () => { const r = stage.getBoundingClientRect(); return { W: Math.max(r.width, 1), H: Math.max(r.height, 1) }; };

  function apply() {
    svg.setAttribute('viewBox', `${vb.x} ${vb.y} ${vb.w} ${vb.h}`);
    onFrame?.(api);
  }
  /** Fit a map-unit box into the stage (keeps the stage aspect ratio). */
  function fit(box) {
    const { W, H } = size(); const A = W / H;
    const w = Math.min(MAX_W, Math.max(MIN_W, Math.max(box.w, box.h * A)));
    const h = w / A;
    return { x: box.cx - w / 2, y: box.cy - h / 2, w, h };
  }
  function set(next) { vb = next; apply(); }

  function flyTo(box, { duration } = {}) {
    const to = fit(box);
    cancelAnimationFrame(anim?.raf);
    if (reduceMotion() || duration === 0) return Promise.resolve(set(to));
    const from = { ...vb };
    const fc = [from.x + from.w / 2, from.y + from.h / 2]; const tc = [to.x + to.w / 2, to.y + to.h / 2];
    const zoomRatio = Math.abs(Math.log(to.w / from.w));
    const ms = duration ?? Math.min(1300, 420 + zoomRatio * 170);
    if (ms <= 0) return Promise.resolve(set(to));
    return new Promise((resolve) => {
      const t0 = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - t0) / ms); const e = ease(t);
        const w = from.w * (to.w / from.w) ** e; const h = w * (to.h / to.w);
        const cx = fc[0] + (tc[0] - fc[0]) * e; const cy = fc[1] + (tc[1] - fc[1]) * e;
        vb = { x: cx - w / 2, y: cy - h / 2, w, h };
        apply();
        if (t < 1) anim = { raf: requestAnimationFrame(step) }; else { anim = null; resolve(); }
      };
      anim = { raf: requestAnimationFrame(step) };
    });
  }

  /** Zoom by factor around a screen point (default: stage centre). */
  function zoomAt(factor, sx, sy) {
    cancelAnimationFrame(anim?.raf); anim = null;
    const { W, H } = size();
    const px = sx ?? W / 2; const py = sy ?? H / 2;
    const mx = vb.x + (px / W) * vb.w; const my = vb.y + (py / H) * vb.h;
    const w = Math.min(MAX_W, Math.max(MIN_W, vb.w / factor)); const h = w * (vb.h / vb.w);
    set({ x: mx - (px / W) * w, y: my - (py / H) * h, w, h });
    onUserMove?.();
  }
  const toScreen = (x, y) => { const { W, H } = size(); return [((x - vb.x) / vb.w) * W, ((y - vb.y) / vb.h) * H]; };

  // ---- input
  stage.addEventListener('wheel', (e) => {
    e.preventDefault();
    const r = stage.getBoundingClientRect();
    zoomAt(Math.exp(-e.deltaY * 0.0016), e.clientX - r.left, e.clientY - r.top);
  }, { passive: false });
  let drag = null;
  stage.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 || e.target.closest('button, a, input, select, .map-panel')) return;
    drag = { x: e.clientX, y: e.clientY, vb: { ...vb }, moved: false, id: e.pointerId };
  });
  stage.addEventListener('pointermove', (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    const dx = e.clientX - drag.x; const dy = e.clientY - drag.y;
    if (!drag.moved && Math.hypot(dx, dy) < 4) return;
    if (!drag.moved) { drag.moved = true; stage.setPointerCapture?.(e.pointerId); stage.classList.add('is-dragging'); cancelAnimationFrame(anim?.raf); }
    const { W, H } = size();
    set({ ...drag.vb, x: drag.vb.x - (dx / W) * drag.vb.w, y: drag.vb.y - (dy / H) * drag.vb.h });
    onUserMove?.();
  });
  const end = () => { if (drag?.moved) stage.classList.remove('is-dragging'); drag = null; };
  stage.addEventListener('pointerup', end);
  stage.addEventListener('pointercancel', end);
  stage.addEventListener('dblclick', (e) => {
    if (e.target.closest('button, a, .map-panel')) return;
    const r = stage.getBoundingClientRect();
    zoomAt(2.2, e.clientX - r.left, e.clientY - r.top);
  });
  stage.addEventListener('keydown', (e) => {
    if (e.target !== stage) return;
    const pan = (fx, fy) => { set({ ...vb, x: vb.x + fx * vb.w * 0.15, y: vb.y + fy * vb.h * 0.15 }); onUserMove?.(); };
    const keys = { '+': () => zoomAt(1.5), '=': () => zoomAt(1.5), '-': () => zoomAt(1 / 1.5), ArrowLeft: () => pan(-1, 0), ArrowRight: () => pan(1, 0), ArrowUp: () => pan(0, -1), ArrowDown: () => pan(0, 1) };
    if (keys[e.key]) { e.preventDefault(); keys[e.key](); }
  });
  let lastSize = null;
  const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(() => {
    const s = size(); if (lastSize && s.W === lastSize.W && s.H === lastSize.H) return; lastSize = s;
    // keep the centre and width, adapt height to the new aspect
    const cx = vb.x + vb.w / 2; const cy = vb.y + vb.h / 2; const h = vb.w * (s.H / s.W);
    set({ x: cx - vb.w / 2, y: cy - h / 2, w: vb.w, h });
  }) : null;
  ro?.observe(stage);

  const api = {
    get view() { return { ...vb }; },
    get busy() { return !!anim; },
    size, fit, set, flyTo, zoomAt, toScreen,
    destroy() { ro?.disconnect(); cancelAnimationFrame(anim?.raf); },
  };
  return api;
}
